import { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useChatStore } from '@/stores';

export interface UseNewChatOptions {
  /** Callback to close mobile menu after navigation */
  onCloseMobile?: () => void;
}

export interface UseNewChatReturn {
  /** Start new chat - shows dialog if conversation is incomplete */
  startNewChat: () => void;
  /** Confirm abandon and start new chat */
  confirmAbandon: () => void;
  /** Cancel abandon dialog */
  cancelAbandon: () => void;
  /** Whether abandon dialog is open */
  showAbandonDialog: boolean;
  /** Whether current conversation is incomplete */
  isIncomplete: boolean;
}

/**
 * useNewChat - 统一的新建对话逻辑
 *
 * 所有新建对话入口应使用此 hook：
 * - Sidebar Home 菜单
 * - Sidebar「+ 新建」按钮
 * - Header 新建按钮
 *
 * 行为：
 * 1. 检查是否有未完成的对话
 * 2. 如果有，弹出确认对话框
 * 3. 确认后重置状态并导航到首页
 */
export function useNewChat(options: UseNewChatOptions = {}): UseNewChatReturn {
  const { onCloseMobile } = options;
  const navigate = useNavigate();
  const location = useLocation();
  const { hasStarted, status, resetFn } = useChatStore();

  const [showAbandonDialog, setShowAbandonDialog] = useState(false);

  // Check if conversation is incomplete (started but not complete)
  const isIncomplete = hasStarted && status !== 'complete' && status !== 'idle';

  // Start new chat - check if need confirmation
  const startNewChat = useCallback(() => {
    if (isIncomplete) {
      // Show confirmation dialog for incomplete conversation
      setShowAbandonDialog(true);
    } else {
      // Directly start new chat
      if (resetFn) {
        resetFn();
      }
      // Only navigate if not already on home page
      if (location.pathname !== '/') {
        navigate('/');
      }
      onCloseMobile?.();
    }
  }, [isIncomplete, resetFn, location.pathname, navigate, onCloseMobile]);

  // Confirm abandon and start new chat
  const confirmAbandon = useCallback(() => {
    setShowAbandonDialog(false);
    if (resetFn) {
      resetFn();
    }
    // Only navigate if not already on home page
    if (location.pathname !== '/') {
      navigate('/');
    }
    onCloseMobile?.();
  }, [resetFn, location.pathname, navigate, onCloseMobile]);

  // Cancel abandon dialog
  const cancelAbandon = useCallback(() => {
    setShowAbandonDialog(false);
  }, []);

  return {
    startNewChat,
    confirmAbandon,
    cancelAbandon,
    showAbandonDialog,
    isIncomplete,
  };
}

